<div class="job-enhancement-page">
  <div class="job-enhancement-title-bar">
    <button type="button" class="back-btn" (click)="onBackToJobs()">
      ← Back to Jobs
    </button>

    <div class="title">Select Enhancement Preset</div>

    <div class="header-right">
      <button type="button" class="next-btn" [disabled]="isSavingPreset || !selectedPreset" (click)="onNext()">
        {{ isSavingPreset ? 'Saving...' : 'Next →' }}
      </button>
    </div>
  </div>

  <div class="job-enhancement-wrapper">
    @if (isLoading) {
    <div class="empty-state">Loading enhancement settings...</div>
    } @else {
    <div class="enhancement-layout">

      <aside class="preset-sidebar">
        <div class="sidebar-section">
          <div class="info-card" style="margin-top: 0; margin-bottom: 16px;">
            <div class="info-icon">i</div>
            <div class="info-content">
              <div class="info-text">
                Select a preset category first. The preset list is refreshed automatically
                when the category changes.
              </div>
            </div>
          </div>

          <label class="field-label" for="presetCategory">
            Preset Category
          </label>

          <select id="presetCategory" class="category-select" [(ngModel)]="selectedPresetCategoryId"
            (change)="onPresetCategoryChanged()">

            <option [ngValue]="null">Select a preset category</option>

            @for (category of presetCategories; track trackByCategoryId($index, category)) {
            <option [ngValue]="category.presetCategoryId">
              {{ category.name }}
            </option>
            }
          </select>

          <p class="field-hint">
            Presets are filtered using the selected preset category.
          </p>
        </div>

        <div class="sidebar-section presets-section">
          <div class="presets-heading">
            <span>Presets</span>

            @if (!isLoadingPresets && selectedPresetCategoryId) {
            <span class="preset-count">{{ totalPresets }}</span>
            }
          </div>

          @if (isLoadingPresets) {
          <div class="list-state">Loading presets...</div>
          } @else if (presetLoadError) {
          <div class="list-state error">
            {{ presetLoadError }}
          </div>
          } @else if (selectedPresetCategoryId && presets.length === 0) {
          <div class="list-state">
            No presets found in this category.
          </div>
          } @else {
          <div class="preset-list">
            @for (preset of presets; track trackByPresetId($index, preset)) {
            <button type="button" class="preset-item" [class.active]="selectedPreset?.presetId === preset.presetId"
              (click)="selectPreset(preset)">

              <span class="preset-icon">✦</span>

              <span class="preset-item-content">
                <strong>{{ preset.name }}</strong>

                @if (preset.description) {
                <small>{{ preset.description }}</small>
                }
              </span>
            </button>
            }
          </div>
          }
        </div>
      </aside>

      <section class="preset-details-card">
        @if (selectedPreset) {

        @if (testEnhancementError) {
        <div class="test-error-message error">
          {{ testEnhancementError }}
        </div>
        }

        <input type="file" accept="image/*" #imageInput style="display: none;" (change)="onImageSelected($event)" />

        @if (enhancementSettings || selectedTestImage) {
        <div class="preset-preview-section">
          <div class="image-comparison-wrapper">
            <div class="image-comparison-container" #comparisonContainer (mousemove)="onContainerMouseMove($event)"
              (mouseenter)="onContainerMouseEnter()" (mouseleave)="onContainerMouseLeave()"
              (contextmenu)="onContainerContextMenu($event)" [class.zoom-active]="isZoomActive">

              <img [src]="currentOriginalImageUrl" alt="Original Image" class="comparison-image original-image" />

              @if (currentEnhancedImageUrl) {
              <div class="enhanced-image-overlay" [style.clip-path]="'inset(0 0 0 ' + sliderValue + '%)'">
                <img [src]="currentEnhancedImageUrl" alt="Enhanced Image" class="comparison-image enhanced-image" />
              </div>

              @if (isZoomActive && isHoveringContainer) {
              <div class="zoom-lens" [style.left.px]="mouseX" [style.top.px]="mouseY">
                <img [src]="currentEnhancedImageUrl" class="zoomed-image" [style.width.px]="zoomImageWidth"
                  [style.height.px]="zoomImageHeight" [style.left.px]="zoomImageX" [style.top.px]="zoomImageY" />
              </div>
              }

              <div class="comparison-divider" [style.left]="sliderValue + '%'">
                <div class="comparison-thumb">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </div>
              </div>

              <input type="range" min="0" max="100" [(ngModel)]="sliderValue" class="comparison-slider" />

              <div class="image-label label-left">Original</div>
              <div class="image-label label-right">Enhanced</div>
              }
            </div>
          </div>

          <div class="preview-actions">
            <button type="button" class="change-image-btn" (click)="openImageSelector()">
              Change Test Image
            </button>
            <button type="button" class="test-enhancement-btn" (click)="onTestEnhancement()"
              [disabled]="isTestingPreset || (!selectedTestImage?.file)">
              Apply Enhancement
            </button>
            @if (isTestingPreset) {
            <span class="testing-status">Testing preset...</span>
            }

            <button type="button" class="toggle-view-btn" [class.active-enhanced]="sliderValue === 0" (click)="toggleOriginalEnhanced()"
              [disabled]="!currentEnhancedImageUrl">
              {{ sliderValue === 0 ? 'Enhanced' : (sliderValue === 100 ? 'Original' : 'Original / Enhanced') }}
            </button>

            <button type="button" class="zoom-btn" [class.active]="isZoomActive" [disabled]="!currentEnhancedImageUrl"
              (click)="toggleZoom()" title="Toggle Zoom">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                <line x1="11" y1="8" x2="11" y2="14"></line>
                <line x1="8" y1="11" x2="14" y2="11"></line>
              </svg>
            </button>

            <button type="button" class="next-btn" [disabled]="isSavingPreset || !selectedPreset" (click)="onNext()">
              {{ isSavingPreset ? 'Saving...' : 'Next →' }}
            </button>
          </div>
        </div>
        } @else {
        <div class="preview-placeholder clickable" (click)="openImageSelector()">
          <div class="preview-placeholder-icon">✦</div>
          <h3>Preset Preview</h3>
          <p>
            Click here to select a test image to preview this preset’s before-and-after result.
          </p>

          <small>
            The preview section will use the Preset Test API and saved last test result APIs.
          </small>
        </div>
        }
        } @else {
        <div class="no-selection-state">
          <div class="no-selection-icon">✦</div>
          <h3>Select a preset</h3>
          <p>
            Choose a preset category, then select a preset from the left panel.
          </p>
        </div>
        }

        @if (savePresetError) {
        <div class="empty-state error mt-16">
          {{ savePresetError }}
        </div>
        }
      </section>
    </div>

    }

    @if (errorMessage) {
    <div class="empty-state error mt-16">
      {{ errorMessage }}
    </div>
    }
  </div>
</div>